import { createFileRoute, Link } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { MarketingLayout, SectionHeader } from "@/components/marketing/Layout";
import { ArrowRight, Check, X } from "lucide-react";

const TITLE = "AI Lead Qualification for B2B: A 2026 Playbook";
const SEO_TITLE = "AI Lead Qualification for B2B Sales | Reality Twin";
const DESC =
  "How AI Twins automate B2B lead scoring and qualification — grounded on your knowledge and synced to your CRM, without brittle intent trees.";
const URL = "https://realitytwin.io/blog/ai-lead-qualification";
const PUBLISHED = "2026-07-06";

export const Route = createFileRoute("/blog/ai-lead-qualification")({
  head: () => {
    const seo = buildSeo({
      path: "/blog/ai-lead-qualification",
      title: SEO_TITLE,
      description: DESC,
      type: "article",
    });
    return {
      ...seo,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: TITLE,
            description: DESC,
            datePublished: PUBLISHED,
            dateModified: PUBLISHED,
            author: { "@type": "Person", name: "Ajdin Ruznic" },
            publisher: {
              "@type": "Organization",
              name: "Reality Twin",
              url: "https://realitytwin.io",
            },
            mainEntityOfPage: URL,
            about: [
              { "@type": "Thing", name: "AI lead qualification" },
              { "@type": "Thing", name: "B2B sales automation" },
              { "@type": "Thing", name: "AI Twin" },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "What is AI lead qualification?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "AI lead qualification uses reasoning models to score and route inbound prospects in real time. Instead of a static form, an AI Twin holds a discovery conversation, checks fit against your ICP and CRM, and hands qualified leads to a human with the context already written up.",
                },
              },
              {
                "@type": "Question",
                name: "How is an AI Twin different from a chatbot for lead qualification?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "A chatbot follows an intent tree and drops the visitor into a form. An AI Twin reasons across your site, pricing, docs and CRM — so it can ask the right qualifying question next, score the account, and update the CRM record itself.",
                },
              },
              {
                "@type": "Question",
                name: "Does the AI Twin sync with our CRM?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes. Reality Twin writes qualified leads, conversation summaries and scoring signals back to the CRM, and reads existing account context so returning contacts skip the intro questions.",
                },
              },
            ],
          }),
        },
      ],
    };
  },
  component: Page,
});

function Page() {
  return (
    <MarketingLayout>
      <article className="py-16 sm:py-24">
        <div className="mx-auto max-w-3xl px-6">
          <div className="font-mono-eyebrow">Guide · 9 min read</div>
          <h1 className="mt-4 font-serif text-[44px] sm:text-[64px] leading-[1.02] tracking-[-0.03em]">
            AI lead qualification{" "}
            <em className="italic text-muted-foreground">for B2B, done right</em>
          </h1>
          <p className="mt-6 text-[18px] leading-[1.6] text-muted-foreground">
            The old B2B funnel qualifies leads with a form and a rep's gut. The new one does it in
            the first conversation — with an AI Twin that already knows your ICP, your pricing, and
            the account's history in the CRM.
          </p>
          <p className="mt-3 text-[13px] text-muted-foreground">
            Published{" "}
            {new Date(PUBLISHED).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}{" "}
            · by Ajdin Ruznic
          </p>
        </div>

        <div className="mx-auto max-w-3xl px-6 mt-14 space-y-10 text-[16.5px] leading-[1.75] text-foreground/90">
          <section>
            <h2 className="font-serif text-[30px] leading-[1.1] tracking-[-0.02em]">
              The short answer
            </h2>
            <p className="mt-4 text-muted-foreground">
              AI lead qualification works when the model is{" "}
              <strong className="text-foreground">grounded</strong> in your own knowledge and{" "}
              <strong className="text-foreground">synced</strong> with the system where the deal
              actually lives. A generic chatbot can't do either — that's why most "AI SDR" projects
              stall after the demo.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-[30px] leading-[1.1] tracking-[-0.02em]">
              Why forms and chatbots stop working
            </h2>
            <p className="mt-4 text-muted-foreground">
              A form asks every visitor the same six questions and drops them into a queue. A
              scripted chatbot adds a chat bubble but keeps the same intent tree. Neither can tell
              whether "we're a fintech in Germany, ~200 people, PCI in scope" is a great fit or a
              bad one — because that judgment lives in your team's head, not in the tool.
            </p>
            <p className="mt-3 text-muted-foreground">
              Reality Twin closes that gap by reading your site, docs and CRM into a single
              knowledge graph and reasoning against it in real time. The qualification questions are
              generated per-conversation, not pre-written.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-[30px] leading-[1.1] tracking-[-0.02em]">
              Side-by-side
            </h2>
            <div className="mt-6 hairline rounded-xl overflow-x-auto">
              <div className="grid grid-cols-3 min-w-[560px] bg-cream text-[13px] font-medium">
                <div className="p-4">Capability</div>
                <div className="p-4 border-l border-border">Forms / chatbots</div>
                <div className="p-4 border-l border-border">Reality Twin</div>
              </div>
              {[
                [
                  "ICP scoring",
                  "Static rules on form fields",
                  "Reasoning across firmographics, intent, and CRM history",
                ],
                [
                  "Discovery",
                  "Same questions for everyone",
                  "Adaptive questions grounded in your playbook",
                ],
                [
                  "Objection handling",
                  "Route to human",
                  "Answers with pricing, security and rollout docs in-line",
                ],
                [
                  "CRM sync",
                  "Zapier + manual cleanup",
                  "Native write-back: lead, summary, score, next step",
                ],
                [
                  "Warm handoff",
                  "Round-robin assign",
                  "Handoff with a written brief the rep can act on",
                ],
                ["Off-hours coverage", "Form goes to a queue", "Qualifies + books meetings 24/7"],
              ].map(([cap, bot, twin]) => (
                <div
                  key={cap}
                  className="grid grid-cols-3 min-w-[560px] border-t border-border text-[14px]"
                >
                  <div className="p-4 font-medium">{cap}</div>
                  <div className="p-4 border-l border-border text-muted-foreground flex gap-2">
                    <X className="h-4 w-4 mt-0.5 text-muted-foreground/60 shrink-0" />
                    {bot}
                  </div>
                  <div className="p-4 border-l border-border flex gap-2">
                    <Check className="h-4 w-4 mt-0.5 shrink-0" />
                    {twin}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="font-serif text-[30px] leading-[1.1] tracking-[-0.02em]">
              1 · Grounded knowledge beats a good prompt
            </h2>
            <p className="mt-4 text-muted-foreground">
              A cleverly-prompted GPT can pretend to qualify a lead for one turn. It falls apart on
              turn three, when the prospect asks something specific — regional pricing, a feature on
              the roadmap, whether you support their compliance framework. If the answer isn't
              grounded in your actual site and docs, the model will invent one.
            </p>
            <p className="mt-3 text-muted-foreground">
              Reality Twin ingests your website, product docs, pricing pages and any files you
              upload into a knowledge graph the Twin reasons against. Every answer cites a source
              the Twin actually read, and every qualification signal is tied back to what the
              prospect said and what your knowledge base supports.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-[30px] leading-[1.1] tracking-[-0.02em]">
              2 · CRM sync is the whole game
            </h2>
            <p className="mt-4 text-muted-foreground">
              An AI qualifier that doesn't write to the CRM is a demo, not a system. The rep still
              has to re-enter everything, and by the next quarter the tool is dark. Reality Twin
              writes the lead, a plain-English conversation summary, the scoring signals it used,
              and the recommended next step directly into the CRM record.
            </p>
            <p className="mt-3 text-muted-foreground">
              It also reads the CRM the other direction: a returning contact from a named account
              skips the intro questions and picks up where the last conversation ended. That's what
              makes it feel like an SDR instead of a bot.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-[30px] leading-[1.1] tracking-[-0.02em]">
              3 · Handoff that a rep actually wants
            </h2>
            <p className="mt-4 text-muted-foreground">
              The number one reason AI qualification fails inside a team isn't the AI — it's the
              handoff. Reps ignore anything that dumps a raw transcript into their inbox. Reality
              Twin ships each qualified lead with a written brief: who they are, what they want,
              what the Twin already answered, the two open questions, and the suggested next step.
              It's the meeting prep the rep would have done anyway.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-[30px] leading-[1.1] tracking-[-0.02em]">
              A four-week rollout plan
            </h2>
            <ol className="mt-4 space-y-3 list-decimal pl-5 text-muted-foreground">
              <li>
                <strong className="text-foreground">Week 1 — Ingest.</strong> Point the Twin at your
                site, docs, and pricing. Review what it learned; correct anything that reads wrong.
              </li>
              <li>
                <strong className="text-foreground">Week 2 — ICP.</strong> Encode your ideal
                customer profile and disqualifiers. The Twin reasons against these instead of a
                form-fill rule.
              </li>
              <li>
                <strong className="text-foreground">Week 3 — CRM.</strong> Connect the CRM. Turn on
                write-back for leads and conversation summaries; read-back for known accounts.
              </li>
              <li>
                <strong className="text-foreground">Week 4 — Handoff.</strong> Wire calendars,
                define what "qualified" means in your funnel, and ship the Twin to the site.
              </li>
            </ol>
          </section>

          <section className="hairline rounded-xl bg-cream p-8 sm:p-10">
            <div className="font-mono-eyebrow">Reality Twin</div>
            <h3 className="mt-3 font-serif text-[28px] leading-[1.1] tracking-[-0.02em]">
              Qualify every inbound lead in the first message.
            </h3>
            <p className="mt-3 text-[15px] text-muted-foreground">
              Reality Twin is available now. Create your account and we'll spin up a Twin from your
              public site and connect it to your CRM in a week.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/auth"
                className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-[13.5px] font-medium text-background hover:opacity-90"
              >
                Get started <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
              </Link>
              <Link
                to="/platform"
                className="inline-flex items-center rounded-full hairline px-4 py-2 text-[13.5px]"
              >
                How the platform works
              </Link>
            </div>
          </section>

          <section>
            <SectionHeader
              eyebrow="FAQ"
              title={
                <>
                  Questions <em className="italic text-muted-foreground">people ask</em>
                </>
              }
            />
            <div className="mt-8 space-y-6">
              {[
                {
                  q: "What is AI lead qualification?",
                  a: "AI lead qualification uses reasoning models to score and route inbound prospects in real time. An AI Twin holds a discovery conversation, checks fit against your ICP and CRM, and hands qualified leads to a human with the context already written up.",
                },
                {
                  q: "How is an AI Twin different from a chatbot for lead qualification?",
                  a: "A chatbot follows an intent tree and drops the visitor into a form. An AI Twin reasons across your site, pricing, docs and CRM — so it can ask the right qualifying question next, score the account, and update the CRM record itself.",
                },
                {
                  q: "Does the AI Twin sync with our CRM?",
                  a: "Yes. Reality Twin writes qualified leads, conversation summaries and scoring signals back to the CRM, and reads existing account context so returning contacts skip the intro questions.",
                },
              ].map((f) => (
                <div key={f.q} className="hairline rounded-xl bg-card p-6">
                  <h3 className="font-serif text-[20px] leading-tight">{f.q}</h3>
                  <p className="mt-2 text-[14.5px] leading-relaxed text-muted-foreground">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </article>
    </MarketingLayout>
  );
}
